import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import { forgetThisDevice } from '@/lib/push'

export type AppRole = 'admin' | 'employee' | 'client'

interface Profile {
  id: string
  email: string
  /** Generated in Postgres from first_name + last_name — read-only. */
  full_name: string | null
  first_name: string | null
  last_name: string | null
  phone: string | null
  role?: AppRole
}




interface AuthValue {
  loading: boolean
  session: Session | null
  user: User | null
  profile: Profile | null
  roles: AppRole[]
  isAdmin: boolean
  isStaff: boolean
  isClient: boolean
  clientId: string | null
  /** Re-reads the profiles row (used after the portal profile form saves). */
  refreshProfile: () => Promise<void>
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<{ error: string | null; alreadyRegistered: boolean; needsConfirmation: boolean }>
  signOut: () => Promise<void>
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>
  updatePassword: (password: string) => Promise<{ error: string | null }>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [roles, setRoles] = useState<AppRole[]>([])
  const [clientId, setClientId] = useState<string | null>(null)

  const loadUserData = useCallback((userId: string) => {
    // Never await inside onAuthStateChange itself — defer to the next tick
    // so the Supabase client's internal lock isn't held across the request.
    setTimeout(async () => {
      // A throw anywhere below used to leave `loading` true forever, which
      // renders as a permanently blank guarded page. Always settle it.
      try {
        // Late client provisioning: profiles + clients rows are only created after
        // email confirmation. If a confirmed client signs in before that runs
        // (or it was interrupted), provision_self() creates them idempotently.
        // Staff profiles are created at invite time, so this is a no-op for them.
        await supabase.rpc('provision_self')
        const [{ data: profileRow }, { data: clientRow }] = await Promise.all([
          supabase.from('profiles').select('id, email, full_name, first_name, last_name, phone, role').eq('id', userId).maybeSingle(),
          supabase.from('clients').select('id').eq('user_id', userId).maybeSingle(),
        ])
        setProfile(profileRow ?? null)
        setRoles(profileRow?.role ? [profileRow.role as AppRole] : [])
        setClientId(clientRow?.id ?? null)
      } catch {
        setProfile(null)
        setRoles([])
        setClientId(null)
      } finally {
        setLoading(false)
      }

      // Best-effort last-seen stamp; profiles_update_self already permits this.
      supabase.from('profiles').update({ last_seen_at: new Date().toISOString() }).eq('id', userId).then(
        () => {},
        () => {}
      )
    }, 0)

  }, [])

  useEffect(() => {
    // Subscribe before fetching the current session, or the initial event is missed.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
      if (newSession?.user) {
        loadUserData(newSession.user.id)
      } else {
        setProfile(null)
        setRoles([])
        setClientId(null)
        setLoading(false)
      }
    })

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      if (data.session?.user) {
        loadUserData(data.session.user.id)
      } else {
        setLoading(false)
      }
    })

    return () => sub.subscription.unsubscribe()
  }, [loadUserData])

  const refreshProfile = useCallback(async () => {
    const { data: auth } = await supabase.auth.getUser()
    const uid = auth.user?.id
    if (!uid) return
    const { data: profileRow } = await supabase
      .from('profiles')
      .select('id, email, full_name, first_name, last_name, phone, role')
      .eq('id', uid)
      .maybeSingle()
    if (profileRow) setProfile(profileRow)
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error ? error.message : null }
  }, [])

  const signUp = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { first_name: firstName, last_name: lastName },
          emailRedirectTo: `${window.location.origin}/portal`,
        },
      })

      // Detecting an existing account differs by Supabase config:
      //  - "Confirm email" OFF -> a real "User already registered" error
      //  - "Confirm email" ON  -> a fake success with an empty identities array
      //                           (Supabase's anti-enumeration behaviour)
      // Handling both means the message stays correct after confirmation is
      // switched on. Note this does surface which emails have accounts.
      const alreadyRegistered =
        /already registered|already been registered|user already exists/i.test(error?.message ?? '') ||
        (!error && (data.user?.identities?.length ?? 1) === 0)

      return {
        error: error && !alreadyRegistered ? error.message : null,
        alreadyRegistered,
        needsConfirmation: !error && !alreadyRegistered && !data.session,
      }
    },
    []
  )

  const signOut = useCallback(async () => {
    // Before the session ends, while there is still one to delete this
    // device's push row with; otherwise a shared computer keeps receiving the
    // previous person's notifications.
    await forgetThisDevice()
    await supabase.auth.signOut()
  }, [])

  const requestPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error: error ? error.message : null }
  }, [])

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    return { error: error ? error.message : null }
  }, [])

  const value = useMemo<AuthValue>(() => {
    const isAdmin = roles.includes('admin')
    const isStaff = isAdmin || roles.includes('employee')
    const isClient = roles.includes('client')
    return {
      loading,
      session,
      user: session?.user ?? null,
      profile,
      roles,
      isAdmin,
      isStaff,
      isClient,
      clientId,
      refreshProfile,
      signIn,
      signUp,
      signOut,
      requestPasswordReset,
      updatePassword,
    }
  }, [loading, session, profile, roles, clientId, refreshProfile, signIn, signUp, signOut, requestPasswordReset, updatePassword])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
